// Zustand store для cookie consent. Single source of truth.
// Persisted в localStorage через storage helpers.

import { create } from 'zustand';
import { recordConsent } from './consentApi';
import { getOrCreateSessionId, getStoredConsent, setStoredConsent } from './storage';
import { ACCEPT_ALL_CHOICES, COOKIE_POLICY_VERSION, DEFAULT_CHOICES } from './types';
import type { ConsentChoices, ConsentSource } from './types';

interface ConsentState {
  choices: ConsentChoices | null;
  hasConsent: boolean;
  bannerVisible: boolean;

  hydrateFromStorage(): void;
  showBanner(): void;
  hideBanner(): void;

  acceptAll(source: ConsentSource): Promise<void>;
  rejectAll(source: ConsentSource): Promise<void>;
  updateChoices(partial: Partial<ConsentChoices>): void;
  saveCurrentChoices(source: ConsentSource): Promise<void>;
}

export const useConsentStore = create<ConsentState>((set, get) => ({
  choices: null,
  hasConsent: false,
  bannerVisible: false,

  hydrateFromStorage() {
    const stored = getStoredConsent();
    if (stored) {
      set({
        choices: stored.choices,
        hasConsent: true,
        bannerVisible: false,
      });
    }
  },

  showBanner() {
    set({ bannerVisible: true });
  },

  hideBanner() {
    set({ bannerVisible: false });
  },

  async acceptAll(source: ConsentSource) {
    const choices = { ...ACCEPT_ALL_CHOICES };
    persistAndSet(set, choices);
    await tryRecord(choices, source);
  },

  async rejectAll(source: ConsentSource) {
    const choices = { ...DEFAULT_CHOICES };
    persistAndSet(set, choices);
    await tryRecord(choices, source);
  },

  updateChoices(partial: Partial<ConsentChoices>) {
    const current = get().choices ?? { ...DEFAULT_CHOICES };
    const merged: ConsentChoices = {
      ...current,
      ...partial,
      essential: true, // invariant
    };
    set({ choices: merged });
  },

  async saveCurrentChoices(source: ConsentSource) {
    const choices = get().choices ?? { ...DEFAULT_CHOICES };
    persistAndSet(set, choices);
    await tryRecord(choices, source);
  },
}));

function persistAndSet(
  set: (partial: Partial<ConsentState>) => void,
  choices: ConsentChoices,
) {
  setStoredConsent({
    choices,
    version: COOKIE_POLICY_VERSION,
    timestamp: new Date().toISOString(),
  });
  set({
    choices,
    hasConsent: true,
    bannerVisible: false,
  });
}

async function tryRecord(choices: ConsentChoices, source: ConsentSource): Promise<void> {
  try {
    const sessionId = getOrCreateSessionId();
    await recordConsent({ choices, source, sessionId });
  } catch (err) {
    // Backend failure не должна ломать UX — locally consent уже записан.
    // eslint-disable-next-line no-console
    console.warn('[consent] backend record failed, kept locally:', err);
  }
}
